import type { Col } from "../art/pal.ts";
import { bayer, hash2 } from "./noise.ts";

/** Key light for all painted props: from the upper-left, slightly toward the viewer. */
export const LIGHT = { x: -0.6, y: -0.7, z: 0.45 };

/**
 * A small software sprite canvas with the primitives the world painters need: shaded volumes that
 * pick tones from a ramp by lighting (with ordered-dither transitions), and a selective outline
 * pass that darkens the silhouette with a tone of the neighbouring colour instead of flat black.
 */
export class Spr {
  readonly w: number;
  readonly h: number;
  readonly img: ImageData;
  readonly d: Uint8ClampedArray;

  constructor(w: number, h: number) {
    this.w = w;
    this.h = h;
    this.img = new ImageData(w, h);
    this.d = this.img.data;
  }

  in(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.w && y < this.h;
  }

  alpha(x: number, y: number): number {
    if (!this.in(x, y)) return 0;
    return this.d[(y * this.w + x) * 4 + 3]!;
  }

  get(x: number, y: number): Col {
    const o = (y * this.w + x) * 4;
    return [this.d[o]!, this.d[o + 1]!, this.d[o + 2]!, this.d[o + 3]!];
  }

  p(x: number, y: number, c: Col): void {
    const xi = x | 0, yi = y | 0;
    if (!this.in(xi, yi)) return;
    const o = (yi * this.w + xi) * 4;
    const a = c[3] / 255;
    if (a >= 1 || this.d[o + 3] === 0) {
      this.d[o] = c[0];
      this.d[o + 1] = c[1];
      this.d[o + 2] = c[2];
      this.d[o + 3] = Math.max(this.d[o + 3]!, c[3]);
      return;
    }
    this.d[o] = this.d[o]! + (c[0] - this.d[o]!) * a;
    this.d[o + 1] = this.d[o + 1]! + (c[1] - this.d[o + 1]!) * a;
    this.d[o + 2] = this.d[o + 2]! + (c[2] - this.d[o + 2]!) * a;
  }

  /** Only paint where something already exists. */
  over(x: number, y: number, c: Col): void {
    if (this.alpha(x | 0, y | 0) > 0) this.p(x, y, c);
  }

  fill(x: number, y: number, w: number, h: number, c: Col): void {
    for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < w; xx++) this.p(x + xx, y + yy, c);
  }

  hline(x: number, y: number, w: number, c: Col): void {
    this.fill(x, y, w, 1, c);
  }

  vline(x: number, y: number, h: number, c: Col): void {
    this.fill(x, y, 1, h, c);
  }

  line(x0: number, y0: number, x1: number, y1: number, c: Col): void {
    let x = Math.round(x0), y = Math.round(y0);
    const ex = Math.round(x1), ey = Math.round(y1);
    const dx = Math.abs(ex - x), dy = -Math.abs(ey - y);
    const sx = x < ex ? 1 : -1, sy = y < ey ? 1 : -1;
    let err = dx + dy;
    for (;;) {
      this.p(x, y, c);
      if (x === ex && y === ey) break;
      const e2 = 2 * err;
      if (e2 >= dy) { err += dy; x += sx; }
      if (e2 <= dx) { err += dx; y += sy; }
    }
  }

  /** Pick a ramp tone for lighting value `lam` (−1..1) with a dithered blend between steps. */
  static tone(ramp: readonly Col[], lam: number, x: number, y: number, bias = 0): Col {
    const n = ramp.length;
    const f = ((1 - lam) / 2) * (n - 1) + bias;
    const base = Math.floor(f);
    const frac = f - base;
    const i = frac > bayer(x, y) ? base + 1 : base;
    return ramp[Math.max(0, Math.min(n - 1, i))]!;
  }

  /** Lit ellipsoid volume. `flat` 0..1 flattens the dome, `bias` shifts toward darker tones. */
  ball(cx: number, cy: number, rx: number, ry: number, ramp: readonly Col[], o: { bias?: number; flat?: number; rim?: boolean; seed?: number; rough?: number } = {}): void {
    const flat = o.flat ?? 0;
    for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
      for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
        const nx = (x + 0.5 - cx) / rx, ny = (y + 0.5 - cy) / ry;
        let d2 = nx * nx + ny * ny;
        if (o.rough) d2 += (hash2(x, y, o.seed ?? 3) - 0.5) * o.rough;
        if (d2 > 1) continue;
        const nz = Math.sqrt(Math.max(0, 1 - d2)) * (1 - flat) + flat;
        let lam = nx * LIGHT.x + ny * LIGHT.y + nz * LIGHT.z;
        lam = lam * 1.25 - 0.1;
        if (o.rim && d2 > 0.72 && nx * LIGHT.x + ny * LIGHT.y < -0.5) lam += 0.55;
        this.p(x, y, Spr.tone(ramp, Math.max(-1, Math.min(1, lam)), x, y, o.bias ?? 0));
      }
    }
  }

  /** Flat ellipse (hard edge). */
  oval(cx: number, cy: number, rx: number, ry: number, c: Col): void {
    for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
      for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
        const nx = (x + 0.5 - cx) / rx, ny = (y + 0.5 - cy) / ry;
        if (nx * nx + ny * ny <= 1) this.p(x, y, c);
      }
    }
  }

  /** Convex/concave polygon fill (even-odd). */
  poly(pts: readonly (readonly [number, number])[], c: Col | ((x: number, y: number) => Col)): void {
    let minY = Infinity, maxY = -Infinity;
    for (const [, y] of pts) { minY = Math.min(minY, y); maxY = Math.max(maxY, y); }
    for (let y = Math.floor(minY); y <= Math.ceil(maxY); y++) {
      const yc = y + 0.5;
      const xs: number[] = [];
      for (let i = 0; i < pts.length; i++) {
        const [ax, ay] = pts[i]!;
        const [bx, by] = pts[(i + 1) % pts.length]!;
        if ((ay <= yc && by > yc) || (by <= yc && ay > yc)) xs.push(ax + ((yc - ay) * (bx - ax)) / (by - ay));
      }
      xs.sort((a, b) => a - b);
      for (let k = 0; k + 1 < xs.length; k += 2) {
        for (let x = Math.round(xs[k]!); x < Math.round(xs[k + 1]!); x++) {
          this.p(x, y, typeof c === "function" ? c(x, y) : c);
        }
      }
    }
  }

  /**
   * Selective outline: every transparent pixel touching the silhouette becomes a darkened version of
   * the neighbour it touches. `k` is the darkening factor; `ink` pulls the colour toward a deep hue.
   */
  outline(ink: Col, k = 0.45, sides: "all" | "bottom" = "all"): void {
    const src = new Uint8ClampedArray(this.d);
    const at = (x: number, y: number) => (x >= 0 && y >= 0 && x < this.w && y < this.h ? src[(y * this.w + x) * 4 + 3]! : 0);
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        if (at(x, y) > 0) continue;
        let nx = -1, ny = -1;
        const cand: [number, number][] = sides === "bottom" ? [[0, -1]] : [[0, -1], [-1, 0], [1, 0], [0, 1]];
        for (const [dx, dy] of cand) {
          if (at(x + dx, y + dy) > 128) { nx = x + dx; ny = y + dy; break; }
        }
        if (nx < 0) continue;
        const o = (ny * this.w + nx) * 4;
        const c: Col = [
          Math.round(src[o]! * k + ink[0] * (1 - k)),
          Math.round(src[o + 1]! * k + ink[1] * (1 - k)),
          Math.round(src[o + 2]! * k + ink[2] * (1 - k)),
          255,
        ];
        this.p(x, y, c);
      }
    }
  }

  /** Darken the lower-right inner edge of the silhouette (form shadow). */
  innerShade(k = 0.78): void {
    const src = new Uint8ClampedArray(this.d);
    const at = (x: number, y: number) => (x >= 0 && y >= 0 && x < this.w && y < this.h ? src[(y * this.w + x) * 4 + 3]! : 0);
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        if (!at(x, y)) continue;
        if (!at(x + 1, y) || !at(x, y + 1)) {
          const o = (y * this.w + x) * 4;
          this.d[o] = this.d[o]! * k;
          this.d[o + 1] = this.d[o + 1]! * k;
          this.d[o + 2] = this.d[o + 2]! * (k + 0.06);
        }
      }
    }
  }
}
